import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Button,
  IconButton,
  LinearProgress,
  Toolbar,
  Tooltip,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import { defineMessages, FormattedMessage } from 'react-intl';
import _ from 'lodash';

import * as actions from '../../../state';
import {
  checkUserPermission as can, // can(user,'action')
} from '../../../utils';
import * as events from '../../../events';
import styles from './styles.module.css';
import Toasters from '../toaster/Toaster';
import BoardName from './BoardName';
import {
  AddCardBtn,
  AddHeaderBtn,
  AppMenu,
  BoardSettingsBtn,
  CopyBoardBtn,
  DeleteBoardBtn,
  EditBoardBtn,
  FavoriteBtn,
  FullScreenBtn,
  HideBtn,
  NightModeToggle,
  ShareBoardBtn,
} from './buttons';
import BackBtnConfirm from './buttons/BackBtnConfirm';
import BackBtn from './buttons/BackBtn';
import { selectLayout } from '../../../state';

const messages = defineMessages({
  ADDING_NEW_CARD: {
    id: 'ADDING_NEW_CARD',
    defaultMessage: 'Adding new card...',
  },
  ADDING_NEW_HEADER: {
    id: 'ADDING_NEW_HEADER',
    defaultMessage: 'Adding new header...',
  },
  EDITING_CARD: {
    id: 'EDITING_CARD',
    defaultMessage: 'Editing card...',
  },
  MAIN_MENU: {
    id: 'MAIN_MENU',
    defaultMessage: 'Main Menu',
  },
  CANCEL: {
    id: 'CANCEL',
    defaultMessage: 'Cancel',
  },
  CHANGES_REVERTED: {
    id: 'CHANGES_REVERTED',
    defaultMessage: 'Changes discarded.',
  },
  SAVE: {
    id: 'SAVE',
    defaultMessage: 'Save',
  },
});

function Header({ toggleSidebar }) {
  const dispatch = useDispatch();

  const layout = useSelector(selectLayout);
  const editHistory = useSelector((state) => state.editHistory);
  const board = useSelector(actions.readStateBoard);
  const user = useSelector(actions.readStateUser);
  const selectedChart = useSelector(actions.readStateSelectedChart);
  const selectedChartId = useSelector((state) => state.board.selectedChartId);

  /* the grid layout ignores the reversion unless it's pushed into an extra cycle.
     * there's probably a better solution than this */
  const resetBoard = () => {
    setTimeout(() => {
      dispatch(actions.selectedChartId(null));
      dispatch(actions.isLoading(false));
      dispatch(events.resetBoard());
    }, 0);
  };

  const backAction = () => {
    dispatch(actions.showRightSidebar(false));

    if (selectedChartId) { // if backing out of card edit
      dispatch(actions.selectedChartId(null));
    } else {
      dispatch(actions.isLoading(true));
      dispatch(actions.editMode(false));

      resetBoard();
    }
  };

  const cancelAction = () => {
    dispatch(actions.isLoading(true));
    dispatch(actions.showRightSidebar(false));

    resetBoard();

    dispatch(actions.showToaster({
      show: true,
      message: <FormattedMessage {...messages.CHANGES_REVERTED} />,
      type: 'success',
    }));
  };

  const saveBoard = () => {
    dispatch(actions.isLoading(true));
    dispatch(events.saveBoard(board));
    dispatch(events.rememberBoard(_.cloneDeep(board)));
  };

  const menuButton = () => (
    <Tooltip disableFocusListener title={<FormattedMessage {...messages.MAIN_MENU} />}>
      <IconButton
          // eslint-disable-next-line
          className={`${styles.menuButton} animate__animated animate__fadeIn ${layout.showLeftSidebar ? styles.menuButtonActive : ''}`}
        color="inherit"
        aria-label="Menu"
        onClick={() => toggleSidebar('left', !layout.showLeftSidebar)}
      >
        <MenuIcon />
      </IconButton>
    </Tooltip>
  );

  /* a card is selected for editing */
  const editCardToolbar = () => {
    let headerMsg = <FormattedMessage {...messages.EDITING_CARD} />;

    if (selectedChart && selectedChart.new) {
      headerMsg = selectedChart.type === 'Header'
        ? <FormattedMessage {...messages.ADDING_NEW_HEADER} />
        : <FormattedMessage {...messages.ADDING_NEW_CARD} />;
    }

    return (
      <Toolbar>
        <div className={styles.flex}>
          { editHistory?.isCardDirty ? <BackBtnConfirm onBack={backAction} /> : <BackBtn onBack={backAction} /> }
        </div>

        <div className={`${styles.editingTitle}`}>
          {headerMsg}
        </div>
      </Toolbar>
    );
  };

  /* board is in edit mode */
  const editModeToolbar = () => (
    <Toolbar>
      <div className={styles.flex}>
        {editHistory.isDirty ? <BackBtnConfirm onBack={backAction} /> : <BackBtn onBack={backAction} />}
      </div>
      <BoardName />
      <div className={styles.boardActions}>
        {(editHistory.isDirty && !layout.isLoading) && (
          <div className={styles.buttonGroup}>
            <Button
              onClick={cancelAction}
              className={styles.mainBtns}
            >
              <FormattedMessage {...messages.CANCEL} />
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={saveBoard}
              className={styles.mainBtns}
              style={{ color: '#fff' }}
            >
              <FormattedMessage {...messages.SAVE} />
            </Button>
          </div>
        )}

        <div className={styles.buttonGroup}>
          <AddCardBtn />
          <AddHeaderBtn />
          <DeleteBoardBtn />
        </div>

        <div className={styles.buttonGroup}>
          <BoardSettingsBtn />
          <FavoriteBtn />
          <ShareBoardBtn />
        </div>

        <div className={styles.buttonGroup}>
          <FullScreenBtn />
          <NightModeToggle />
          <AppMenu />
        </div>
      </div>
    </Toolbar>
  );

  /* board is in public mode */
  const publicModeToolbar = () => (
    <Toolbar>
      <div className={styles.flex}>
        <img src={layout.logo} alt="Logo" className={styles.headerLogo} style={{ position: 'absolute' }} />
      </div>
      { board && <BoardName /> }
      { board && (
        <div className={styles.boardActions}>
          <div className={styles.buttonGroup}>
            <FullScreenBtn />
            <NightModeToggle />
          </div>
        </div>
      )}
    </Toolbar>
  );

  /* board is shared but edit permission not allowed */
  const viewOnlyToolbar = () => (
    <Toolbar>
      <div className={styles.flex}>
        {menuButton()}
        <img src={layout.logo} alt="Logo" className={styles.headerLogo} />
      </div>
      { board && <BoardName /> }
      { board && (
        <div className={styles.boardActions}>
          { board.owner_type === 0
            && can(user, 'EDIT_BOARDS') // allow copy of stock boards
            && (
              <div className={styles.buttonGroup}>
                <CopyBoardBtn />
              </div>
            )}
          <div className={styles.buttonGroup}>
            { board.id && <HideBtn /> }
            { board.id && <FavoriteBtn /> }
          </div>
          <div className={styles.buttonGroup}>
            <FullScreenBtn />
            <NightModeToggle />
            <AppMenu />
          </div>
        </div>
      )}
    </Toolbar>
  );

  const errorToolbar = () => (
    <Toolbar>
      <div className={styles.flex}>
        {menuButton()}
        <img src={layout.logo} alt="Logo" className={styles.headerLogo} />
      </div>
      {board && <BoardName />}
    </Toolbar>
  );

  /* board is in view mode but I have edit rights */
  const standardToolbar = () => (
    <Toolbar>
      <div className={styles.flex}>
        {menuButton()}
        <img src={layout.logo} alt="Logo" className={styles.headerLogo} />
      </div>
      { board && <BoardName /> }
      { board && (
        <div className={styles.boardActions}>
          <div className={styles.buttonGroup}>
            <EditBoardBtn />
            <CopyBoardBtn />
            <DeleteBoardBtn />
          </div>
          <div className={styles.buttonGroup}>
            <BoardSettingsBtn />
            <FavoriteBtn />
            <ShareBoardBtn />
          </div>
          <div className={styles.buttonGroup}>
            <FullScreenBtn />
            <NightModeToggle />
            <AppMenu />
          </div>
        </div>
      )}
    </Toolbar>
  );

  return (
    <>
      <AppBar position="absolute" className={`${styles.header} ${layout.editMode ? styles.editHeader : ''}`}>
        {(layout.editMode && selectedChartId && editCardToolbar())
          || (layout.editMode && board && editModeToolbar())
          || (layout.publicMode && publicModeToolbar())
          || (layout.viewOnly && board && board.error && errorToolbar())
          || (layout.viewOnly && viewOnlyToolbar())
          || standardToolbar()}
      </AppBar>

      {layout.isLoading && <LinearProgress className={styles.isLoading} />}

      {layout.showToaster.show
        && (
          <Toasters
            toasterMessage={layout.showToaster.message}
            toasterType={layout.showToaster.type}
            toasterAction={layout.showToaster.action}
          />
        )}
    </>
  );
}

export default Header;
